import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { RANKED_SCARCITY } from '@sorare/core/src/lib/scarcity';

import { MyCard } from './MyCard';
import type {
  MyCards_anyCardInterface,
  MyCards_anyGameInterface,
} from './__generated__/index.graphql';

const StyledPlayerRow = styled(ButtonBase)`
  transition: opacity 0.2s;
  margin-bottom: var(--intermediate-unit);
`;

const PlayerList = styled(Vertical)`
  &:hover ${StyledPlayerRow} {
    opacity: 0.7;
  }
  &:hover ${StyledPlayerRow}:hover {
    opacity: 1;
  }
`;

type Props = {
  cards: MyCards_anyCardInterface[];
  game: MyCards_anyGameInterface;
  onPlayerDetailsClick?: (playerSlug: string) => void;
};

export const MyCards = ({ cards, game, onPlayerDetailsClick }: Props) => {
  const [showAllCards, setShowAllCards] = useState(false);
  if (cards.length === 0) {
    return null;
  }
  const itemsToDisplay = 3;

  const sortedCards = [...cards].sort((a, b) => {
    const {
      rarityTyped: rA,
      anyPlayer: { playerGameScore: pgA },
    } = a;

    const {
      rarityTyped: rB,
      anyPlayer: { playerGameScore: pgB },
    } = b;

    if (rA !== rB) {
      return RANKED_SCARCITY[rB] - RANKED_SCARCITY[rA];
    }

    return (pgB?.score || 0) - (pgA?.score || 0);
  });

  const visibleCards = sortedCards.slice(0, itemsToDisplay);
  const remainingCards = sortedCards.slice(itemsToDisplay);
  return (
    <Vertical gap={3}>
      <HeadlineM bold>
        <FormattedMessage {...navLabels.myCards} />
      </HeadlineM>
      <Vertical gap={0}>
        <PlayerList gap={0}>
          {visibleCards.map(card => {
            const appearance = game?.mySo5Appearances.find(
              a => a.anyCard?.slug === card.slug
            );
            return (
              <StyledPlayerRow
                key={card.slug}
                onClick={() => onPlayerDetailsClick?.(card.anyPlayer.slug)}
              >
                <MyCard card={card} appearance={appearance} />
              </StyledPlayerRow>
            );
          })}
        </PlayerList>
        {remainingCards.length > 0 && (
          <Vertical gap={0}>
            <Collapse open={showAllCards} gap={0}>
              <PlayerList gap={0}>
                {remainingCards.map(card => {
                  const appearance = game?.mySo5Appearances.find(
                    a => a.anyCard?.slug === card.slug
                  );
                  return (
                    <StyledPlayerRow
                      key={card.slug}
                      onClick={() =>
                        onPlayerDetailsClick?.(card.anyPlayer.slug)
                      }
                    >
                      <MyCard card={card} appearance={appearance} />
                    </StyledPlayerRow>
                  );
                })}
              </PlayerList>
            </Collapse>
            <Button
              color="quaternary"
              size="medium"
              onClick={() => setShowAllCards(v => !v)}
            >
              <FormattedMessage
                {...(showAllCards ? glossary.showLess : glossary.showMore)}
              />
            </Button>
          </Vertical>
        )}
      </Vertical>
    </Vertical>
  );
};

MyCards.fragments = {
  anyCardInterface: gql`
    fragment MyCards_anyCardInterface on AnyCardInterface {
      slug
      rarityTyped
      anyPlayer {
        slug
        playerGameScore(gameId: $gameIdWithoutPrefix) {
          id
          score
        }
      }
      ...MyCard_anyCardInterface
    }
    ${MyCard.fragments.anyCardInterface}
  ` as TypedDocumentNode<MyCards_anyCardInterface>,
  anyGameInterface: gql`
    fragment MyCards_anyGameInterface on AnyGameInterface {
      id
      mySo5Appearances {
        id
        anyCard {
          slug
        }
        ...MyCard_So5Appearance
      }
    }
    ${MyCard.fragments.appearance}
  ` as TypedDocumentNode<MyCards_anyGameInterface>,
};
